import { Plugin } from 'prosemirror-state'

type ListItem = {
  text: string
  level: number
  isOrdered: boolean
}

function getListLevel(element: Element): number | null {
  const style = element.getAttribute('style') || ''

  // Check for Word's mso-list level marker
  const wordLevelMatch = style.match(/mso-list:[^;]*level(\d+)/)
  if (wordLevelMatch !== null) {
    return parseInt(wordLevelMatch[1], 10)
  }

  // Check margin-left in inches (Word format)
  const marginInchesMatch = style.match(/margin-left:\s*([0-9.]+)in/)
  if (marginInchesMatch !== null) {
    return Math.round(parseFloat(marginInchesMatch[1]) / 0.5)
  }

  // Check margin-left in points (Word format)
  const marginPointsMatch = style.match(/margin-left:\s*([0-9.]+)pt/)
  if (marginPointsMatch !== null) {
    return Math.max(1, Math.round(parseFloat(marginPointsMatch[1]) / 36))
  }

  return null
}

function cleanWordBullets(element: Element): string {
  const clone = element.cloneNode(true) as Element

  // Remove Word's hidden comment markers
  const walker = document.createTreeWalker(clone, NodeFilter.SHOW_COMMENT, null)
  const comments: Node[] = []
  let node = walker.nextNode()
  while (node !== null) {
    comments.push(node)
    node = walker.nextNode()
  }
  comments.forEach((comment) => comment.parentNode?.removeChild(comment))

  // Remove spans that contain Word's bullet/number formatting
  clone.querySelectorAll('span[style*="mso-list"]').forEach((span) => span.remove())

  // Remove spans that only contain bullet characters
  clone.querySelectorAll('span').forEach((span) => {
    const text = span.textContent?.trim() || ''
    const isBulletChar = /^[·•○●◦▪▫■\-o§]$/.test(text)
    const hasWordStyle = (span.getAttribute('style') || '').match(/mso-list|Symbol|Courier|Wingdings/)

    if ((isBulletChar || text === '') && hasWordStyle) {
      span.remove()
    }
  })

  return clone.textContent?.trim() || ''
}

function isNumberedList(element: Element): boolean {
  const style = element.getAttribute('style') || ''

  // Check Word's list definition for number format
  const wordListMatch = style.match(/mso-list:[^;]*l(\d+)\s+level(\d+)/)
  if (wordListMatch !== null) {
    const fullHTML = element.ownerDocument.documentElement.outerHTML
    const hasNumberFormat = fullHTML.includes(`@list l${wordListMatch[1]}`) && !fullHTML.includes('mso-level-number-format:bullet')
    if (hasNumberFormat) {
      return true
    }
  }

  // Fallback: check if text starts with a number
  const text = element.textContent || ''
  return /^\s*\d+[.)]\s/.test(text)
}

function extractListItems(html: string): ListItem[] {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const wordListParagraphs = doc.querySelectorAll('p[class*="MsoListParagraph"], p[style*="mso-list"]')

  // If no Word list paragraphs found, return empty (let browser handle regular lists)
  if (wordListParagraphs.length === 0) {
    return []
  }

  const items: ListItem[] = []

  wordListParagraphs.forEach((paragraph) => {
    const level = getListLevel(paragraph)
    if (level === null) {
      return
    }

    const text = cleanWordBullets(paragraph)
    if (text === '') {
      return
    }

    items.push({
      text,
      level,
      isOrdered: isNumberedList(paragraph),
    })
  })

  return items
}

function buildNestedLists(items: ListItem[]): string {
  if (items.length === 0) {
    return ''
  }

  const doc = new DOMParser().parseFromString('', 'text/html')
  const listStack: Array<{ list: Element; level: number }> = []

  items.forEach((item) => {
    // Pop lists from stack that are at same or deeper level
    while (listStack.length > 0 && listStack[listStack.length - 1].level >= item.level) {
      listStack.pop()
    }

    const listType = item.isOrdered ? 'ol' : 'ul'
    let targetList: Element

    if (listStack.length === 0) {
      // Root level: find or create matching list in body
      const existingList = Array.from(doc.body.children).find((child) => child.tagName.toLowerCase() === listType)

      if (existingList !== undefined) {
        targetList = existingList
      } else {
        targetList = doc.createElement(listType)
        doc.body.appendChild(targetList)
      }
    } else {
      // Nested level: add to last item of parent list
      const parentList = listStack[listStack.length - 1].list
      const lastItem = parentList.lastElementChild

      if (lastItem !== null) {
        const existingNestedList = Array.from(lastItem.children).find((child) => child.tagName.toLowerCase() === listType)

        if (existingNestedList !== undefined) {
          targetList = existingNestedList
        } else {
          targetList = doc.createElement(listType)
          lastItem.appendChild(targetList)
        }
      } else {
        // No parent item exists, create root list
        targetList = doc.createElement(listType)
        doc.body.appendChild(targetList)
      }
    }

    const listItem = doc.createElement('li')
    listItem.textContent = item.text
    targetList.appendChild(listItem)

    listStack.push({ list: targetList, level: item.level })
  })

  return doc.body.innerHTML
}

function stripUnsupportedFormatting(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const allowedTags = ['p', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'br']

  doc.body.querySelectorAll('*').forEach((element) => {
    if (element instanceof HTMLElement) {
      // Remove all inline styles and classes
      element.removeAttribute('style')
      element.removeAttribute('class')

      // Replace unsupported tags with span
      if (!allowedTags.includes(element.tagName.toLowerCase())) {
        const span = doc.createElement('span')
        while (element.firstChild !== null) {
          span.appendChild(element.firstChild)
        }
        element.replaceWith(span)
      }
    }
  })

  return doc.body.innerHTML
}

export function createPasteHandlerPlugin(): Plugin {
  return new Plugin({
    props: {
      transformPastedHTML(html: string): string {
        try {
          const listItems = extractListItems(html)

          // If we found Word list items, transform them
          let transformedHtml = html
          if (listItems.length > 0) {
            transformedHtml = buildNestedLists(listItems)
          }

          return stripUnsupportedFormatting(transformedHtml)
        } catch (error) {
          console.error('Error transforming pasted HTML:', error)
          return html
        }
      },
    },
  })
}
